import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { getPlanes, updatePlan, deletePlan } from "../../services/divinaPlanesService";
import { getErrorSummary } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { KpiCard } from "../../components/ui/KpiCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { PlusCircle, CreditCard, Layers, FileText, Archive, Trash2, Settings, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  GroupedTable,
  StatusTabs,
  GroupToggle,
  StateToggle,
  StatusPill,
  TipoBadge,
  DocsCell,
  RowActions,
  ActionButton,
  CompanyChip,
} from "../../components/product_config/GroupedTable";
import { useSupabaseClient } from "../../lib/supabase";

const ARCHIVOS_REQ = ["Condicionado", "Cuadro de coberturas", "Certificado condicionado"];

const isComplementario = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const docsCount = (plan) =>
  [plan.condicionado_archivo_id, plan.cuadro_coberturas_archivo_id, plan.certcondicionado_archivo_id].filter(Boolean).length;

/* True si el plan no aplica cargas familiares (descripcion_json.aplica_cargas === false) */
const aplicaCargasFalse = (plan) => {
  const dj = typeof plan.descripcion_json === 'string'
    ? (() => { try { return JSON.parse(plan.descripcion_json); } catch { return {}; } })()
    : (plan.descripcion_json || {});
  return dj.aplica_cargas === false;
};


const formatPrecio = (plan) => {
  const raw = plan.precios?.[0]?.precio_uf;
  const n = Number(raw);
  if (raw == null || Number.isNaN(n)) return null;
  return `UF ${n.toLocaleString("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function DivinaPlanesList() {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("todos");
  const [tipoTab, setTipoTab] = useState("all");
  const [groupBy, setGroupBy] = useState("producto");
  const [q, setQ] = useState("");
  const [updatingById, setUpdatingById] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const supabaseClient = useSupabaseClient();

  useEffect(() => {
    if (supabaseClient) loadPlanes();
  }, [supabaseClient]);

  const loadPlanes = async () => {
    try {
      setLoading(true);
      const data = await getPlanes(supabaseClient);
      setPlanes(data);
    } catch (error) {
      console.error("Error loading planes:", error);
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => ({
    todos: planes.filter((p) => !p.archivado).length,
    activos: planes.filter((p) => p.activo === true && p.archivado !== true).length,
    inactivos: planes.filter((p) => p.activo === false && p.archivado !== true).length,
    archivados: planes.filter((p) => p.archivado === true).length,
  }), [planes]);

  const kpis = useMemo(() => {
    const vivos = planes.filter((p) => !p.archivado);
    const principales = vivos.filter((p) => !isComplementario(p.es_complementario)).length;
    const pendientes = vivos.filter((p) => docsCount(p) < 3).length;
    return { principales, pendientes };
  }, [planes]);

  const filtered = useMemo(() => {
    let out = planes.slice();
    if (tab === "activos") out = out.filter((p) => p.activo === true && p.archivado !== true);
    else if (tab === "inactivos") out = out.filter((p) => p.activo === false && p.archivado !== true);
    else if (tab === "archivados") out = out.filter((p) => p.archivado === true);
    else out = out.filter((p) => p.archivado !== true);

    if (tipoTab === "principal") out = out.filter((p) => !isComplementario(p.es_complementario));
    else if (tipoTab === "complementario") out = out.filter((p) => isComplementario(p.es_complementario));

    if (q.trim()) {
      const n = q.trim().toLowerCase();
      out = out.filter((p) =>
        (p.nombre || "").toLowerCase().includes(n) ||
        (p.producto?.nombre || "").toLowerCase().includes(n) ||
        (p.producto?.compania?.nombre_corto || "").toLowerCase().includes(n)
      );
    }
    return out;
  }, [planes, tab, tipoTab, q]);

  const handleStatusToggle = async (plan, newStatus) => {
    const previous = Boolean(plan.activo);
    const next = Boolean(newStatus);
    if (previous === next) return;
    // No se puede activar un plan cuyo producto está inactivo: sería un estado
    // inconsistente (un plan "vendible" bajo un producto que no lo está). Solo
    // se bloquea al ACTIVAR; desactivar siempre se permite. `activo === false`
    // explícito — un producto con `activo` null/undefined (datos viejos) no
    // bloquea.
    if (next && plan.producto?.activo === false) {
      toast.error(
        `No se puede activar este plan: el producto "${plan.producto?.nombre || 'asociado'}" está inactivo. Actívalo primero.`
      );
      return;
    }
    try {
      setUpdatingById((s) => ({ ...s, [plan.id]: true }));
      setPlanes((prev) => prev.map((p) => (p.id === plan.id ? { ...p, activo: next } : p)));
      await updatePlan(plan.id, { activo: next }, supabaseClient);
    } catch (error) {
      const summary = getErrorSummary(error);
      setPlanes((prev) => prev.map((p) => (p.id === plan.id ? { ...p, activo: previous } : p)));
      toast.error(`No se pudo actualizar el estado: ${summary.message}`);
    } finally {
      setUpdatingById((s) => ({ ...s, [plan.id]: false }));
    }
  };

  const handleArchiveToggle = async (plan, archived) => {
    const previous = Boolean(plan.archivado);
    const next = Boolean(archived);
    if (previous === next) return;
    try {
      setUpdatingById((s) => ({ ...s, [plan.id]: true }));
      setPlanes((prev) => prev.map((p) => (p.id === plan.id ? { ...p, archivado: next } : p)));
      await updatePlan(plan.id, { archivado: next }, supabaseClient);
    } catch (error) {
      const summary = getErrorSummary(error);
      setPlanes((prev) => prev.map((p) => (p.id === plan.id ? { ...p, archivado: previous } : p)));
      toast.error(`No se pudo archivar el plan: ${summary.message}`);
    } finally {
      setUpdatingById((s) => ({ ...s, [plan.id]: false }));
    }
  };

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;
    try {
      await deletePlan(planToDelete.id, supabaseClient);
      setPlanes((prev) => prev.filter((p) => p.id !== planToDelete.id));
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    } catch (error) {
      toast.error(`No se pudo eliminar el plan: ${getErrorSummary(error).message}`);
    }
  };

  const columns = useMemo(() => [
    {
      key: "nombre",
      label: "Plan",
      minWidth: 230,
      cellStyle: { whiteSpace: "normal" },
      render: (r) => {
        const compl = isComplementario(r.es_complementario);
        const bases = compl && Array.isArray(r.planes_base) && r.planes_base.length
          ? r.planes_base.map((id) => planes.find((p) => p.id === id)?.nombre || "Plan base").join(", ")
          : null;
        return (
          <div style={{ minWidth: 0, lineHeight: 1.3 }}>
            <Link
              to={`/product_config/planes/edit/${r.id}`}
              title="Editar plan"
              style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "#007D6E", textDecoration: "none", cursor: "pointer" }}
            >
              {r.nombre}
            </Link>
            {groupBy !== "producto" && r.producto?.nombre && (
              <div style={{ fontSize: 11.5, color: "#8D8D8D" }}>{r.producto.nombre}</div>
            )}
            {groupBy === "producto" && r.producto?.compania?.nombre_corto && (
              <div style={{ fontSize: 11.5, color: "#8D8D8D" }}>{r.producto.compania.nombre_corto}</div>
            )}
            {bases && (
              <div style={{ fontSize: 11.5, color: "#7E22CE", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 360 }}>
                Se agrega a: {bases}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "tipo",
      label: "Tipo",
      width: 150,
      render: (r) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <TipoBadge complementario={isComplementario(r.es_complementario)} />
          {aplicaCargasFalse(r) && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#9A3412", background: "#FFEDD5", borderRadius: 6, padding: "1px 6px" }}>
              Sin cargas
            </span>
          )}
        </div>
      ),
    },
    {
      key: "docs",
      label: "Archivos oblig.",
      width: 140,
      render: (r) => <DocsCell docs={docsCount(r)} total={3} names={ARCHIVOS_REQ} />,
    },
    {
      key: "precio",
      label: "Prima",
      width: 110,
      align: "right",
      numeric: true,
      render: (r) => {
        const precio = formatPrecio(r);
        return precio ? (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A" }}>{precio}</div>
            <div style={{ fontSize: 11, color: "#8D8D8D" }}>/mes</div>
          </div>
        ) : (
          <span style={{ color: "#8D8D8D", fontSize: 13 }}>—</span>
        );
      },
    },
    {
      key: "estado",
      label: "Estado",
      width: 230,
      render: (r) => {
        if (r.archivado) return <StatusPill estado="archivado" />;
        // Producto inactivo: el toggle solo puede APAGAR el plan, no encenderlo.
        // Si el plan está apagado, se bloquea del todo (no se puede activar);
        // si está encendido —estado inconsistente heredado— se deja apagar pero
        // se avisa por qué. El mensaje cambia según el caso.
        const productoInactivo = r.producto?.activo === false;
        const bloquearActivar = productoInactivo && !r.activo;
        const nombreProd = r.producto?.nombre || "asociado";
        // Plan activo + producto inactivo → inconsistencia a corregir.
        // Plan inactivo + producto inactivo → no se puede activar (bloqueado).
        const advertencia = r.activo
          ? `Inconsistencia: este plan está activo pero su producto "${nombreProd}" está inactivo. Desactívalo o reactiva el producto.`
          : `No se puede activar: el producto "${nombreProd}" está inactivo. Actívalo primero.`;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <StateToggle
                on={r.activo}
                disabled={Boolean(updatingById[r.id]) || bloquearActivar}
                onChange={(v) => handleStatusToggle(r, v)}
              />
              <StatusPill estado={r.activo ? "activo" : "inactivo"} />
            </div>
            {productoInactivo && (
              <div
                title={advertencia}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 5,
                  fontSize: 11,
                  lineHeight: 1.3,
                  fontWeight: 600,
                  color: "#B45309",
                  background: "#FEF3C7",
                  border: "1px solid #FCD34D",
                  borderRadius: 5,
                  padding: "3px 7px",
                  maxWidth: 210,
                }}
              >
                <AlertTriangle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 1 }} />
                <span>Producto inactivo</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "_acc",
      label: "",
      width: 130,
      align: "right",
      render: (r) => (
        <RowActions>
          <ActionButton icon={Settings} title="Configurar" to={`/product_config/planes/edit/${r.id}`} />
          {!r.archivado && (
            <ActionButton icon={Archive} title="Archivar" disabled={Boolean(updatingById[r.id])} onClick={() => handleArchiveToggle(r, true)} />
          )}
          <ActionButton icon={Trash2} title="Eliminar" danger onClick={() => handleDeleteClick(r)} />
        </RowActions>
      ),
    },
  ], [groupBy, updatingById, planes]);

  /* ── Grupos por producto o por compañía ─────────────────────────────────── */
  const groups = useMemo(() => {
    const decorate = (p) => ({ ...p, _dim: !p.archivado && p.activo === false });

    if (groupBy === "ninguno") {
      return [{ id: "all", title: "Todos los planes", count: filtered.length, unit: "plan", unitPlural: "planes", rows: filtered.map(decorate) }];
    }

    if (groupBy === "compania") {
      const byComp = {};
      filtered.forEach((p) => {
        const key = p.producto?.compania?.id || "__sin_compania__";
        (byComp[key] = byComp[key] || []).push(p);
      });
      const keys = Object.keys(byComp).sort((a, b) => {
        const na = byComp[a][0]?.producto?.compania?.nombre_corto || "";
        const nb = byComp[b][0]?.producto?.compania?.nombre_corto || "";
        return na.localeCompare(nb, "es");
      });
      return keys.map((key) => {
        const list = byComp[key];
        const activos = list.filter((p) => p.activo === true && !p.archivado).length;
        const compNombre = list[0]?.producto?.compania?.nombre_corto || "Sin compañía";
        return {
          id: key,
          title: compNombre,
          count: list.length,
          unit: "plan",
          unitPlural: "planes",
          meta: <span>{activos} {activos === 1 ? "activo" : "activos"}</span>,
          rows: list.map(decorate),
        };
      });
    }

    // groupBy === "producto"
    const byProd = {};
    filtered.forEach((p) => {
      const key = p.producto?.id || "__sin_producto__";
      (byProd[key] = byProd[key] || []).push(p);
    });
    const keys = Object.keys(byProd).sort((a, b) => {
      const na = byProd[a][0]?.producto?.nombre || "";
      const nb = byProd[b][0]?.producto?.nombre || "";
      return na.localeCompare(nb, "es");
    });
    return keys.map((key) => {
      const list = byProd[key];
      const activos = list.filter((p) => p.activo === true && !p.archivado).length;
      const prodNombre = list[0]?.producto?.nombre || "Sin producto";
      const compNombre = list[0]?.producto?.compania?.nombre_corto;
      return {
        id: key,
        title: prodNombre,
        count: list.length,
        unit: "plan",
        unitPlural: "planes",
        meta: (
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {activos} {activos === 1 ? "activo" : "activos"}
            {compNombre && <CompanyChip name={compNombre} />}
          </span>
        ),
        rows: list.map(decorate),
      };
    });
  }, [filtered, groupBy]);

  return (
    <div className="@container/main p-6 space-y-6 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[27px] font-bold tracking-[-0.03em] text-[#1A1A1A] dark:text-white">Planes</h1>
          <p className="text-sm text-[#54585A] dark:text-gray-400 mt-1">Planes de Divina agrupados por producto y compañía</p>
        </div>
        <Link to="/product_config/planes/new">
          <Button className="flex items-center gap-2">
            <PlusCircle className="h-4 w-4" />
            Nuevo plan
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-[14px] md:grid-cols-3">
        <KpiCard label="Planes activos" value={String(counts.activos)} icon={<CreditCard />} badge={{ text: `de ${counts.todos} totales`, direction: "neu" }} />
        <KpiCard label="Planes principales" value={String(kpis.principales)} icon={<Layers />} badge={{ text: "el resto, complementarios", direction: "neu" }} />
        <KpiCard label="Con archivos pendientes" value={String(kpis.pendientes)} icon={<FileText />} badge={{ text: "requieren documentación", direction: kpis.pendientes > 0 ? "warn" : "neu" }} />
      </div>

      <StatusTabs
        active={tab}
        onChange={setTab}
        tabs={[
          { value: "todos", label: "Todos", count: counts.todos },
          { value: "activos", label: "Activos", count: counts.activos },
          { value: "inactivos", label: "Inactivos", count: counts.inactivos },
          { value: "archivados", label: "Archivados", count: counts.archivados },
        ]}
      />

      {loading ? (
        <div className="grid grid-cols-1 gap-[14px] md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-[#E0E0E0] bg-white p-5 space-y-3 animate-pulse">
              <div className="h-4 bg-slate-100 rounded w-1/2" />
              <div className="h-8 bg-slate-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : (
        <GroupedTable
          columns={columns}
          groups={groups}
          search="Buscar plan, producto o compañía…"
          q={q}
          setQ={setQ}
          groupToggle={
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <GroupToggle
                value={tipoTab}
                onChange={setTipoTab}
                options={[
                  { value: "all", label: "Todos" },
                  { value: "principal", label: "Principal" },
                  { value: "complementario", label: "Compl." },
                ]}
              />
              <GroupToggle
                label="Agrupar:"
                value={groupBy}
                onChange={setGroupBy}
                options={[
                  { value: "producto", label: "Por producto" },
                  { value: "compania", label: "Por compañía" },
                  { value: "ninguno", label: "Ninguno" },
                ]}
              />
            </div>
          }
          totalShown={filtered.length}
          totalAll={counts.todos}
          empty={{ title: "Sin planes", text: "No hay planes que coincidan con este filtro." }}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el plan "{planToDelete?.nombre}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
